import { FC, PropsWithChildren, useEffect } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Rnd, RndResizeCallback } from 'react-rnd';
import { Button, Group, IconHandle, Token, Typography } from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { Article } from 'components/datamodel/content/Article/Article';
import { LinkedItem } from 'components/datamodel/content/LinkedItem/LinkedItem';
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
import { DRAGGABLE_PREFIXES, DROPPABLE_ID_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import {
  ArticleItem,
  GraphicLinkedItem as GraphicLinkedItemModel,
  LinkedItem as LinkedItemModel,
  MobileLinkedItem
} from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import styles from './Scratchpad.module.scss';
import { useScratchpadContext } from './scratchpad-context';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

interface ScratchpadProps {
  width: number | undefined;
  x: number | undefined;
  isRefetching?: boolean;
}

const CARD_HEIGHT = 109.6;
const MAX_SCRATCHPAD_HEIGHT = 700;
const MIN_SCRATCHPAD_HEIGHT = 150;
const SCRATCHPAD_TOP_CONTENT_OFFSET = 46;

const Scratchpad: FC<PropsWithChildren<ScratchpadProps>> = ({ width = 0, x = 0, isRefetching = false }) => {
  const { renderActions: renderMenuActions } = useContextMenuActions();
  const scratchpadCtx = useScratchpadContext();
  const { data } = usePublicationSettings();
  const { state } = usePageLock();
  const publishingState = state.publishing;
  const { isReadOnly } = publishingState;

  // Reset height to MIN_SCRATCHPAD_HEIGHT on unmount to prevent resize issues
  useEffect(
    () => () => {
      scratchpadCtx?.setHeight(MIN_SCRATCHPAD_HEIGHT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scratchpadCtx?.setHeight]
  );

  if (data && scratchpadCtx) {
    const { height, setHeight, scrollTop, setScrollTop, scratchpad, getLastStashedGroup, restoreLastStashedGroup } =
      scratchpadCtx;
    const { rulesConfig } = data.publicationSetting;
    const lastStashedGroup = getLastStashedGroup();

    const handleResize: RndResizeCallback = (_e, _direction, ref) => {
      setHeight(ref.offsetHeight);
    };

    return (
      <div
        className={styles.scratchpadContainer}
        style={{
          left: x
        }}
      >
        <Rnd
          dragAxis="y"
          disableDragging
          resizeHandleClasses={{
            top: styles.scratchpadHandle
          }}
          size={{
            height,
            width
          }}
          maxHeight={MAX_SCRATCHPAD_HEIGHT}
          minHeight={MIN_SCRATCHPAD_HEIGHT}
          maxWidth={width}
          bounds="window"
          enableResizing
          onResizeStop={handleResize}
          className={styles.scratchpadResizer}
          resizeHandleComponent={{
            top: (
              <div className={styles.scratchpadHandle}>
                <IconHandle className={styles.scratchpadIconHandle} />
              </div>
            )
          }}
        >
          <div
            className={styles.scratchpadContent}
            onScroll={(e) => {
              setScrollTop(e.currentTarget.scrollTop);
            }}
            style={{
              overflowY: scratchpad.length ? 'auto' : 'initial'
            }}
          >
            <div className={styles.scratchpadInnerContent}>
              <Group margin={{ top: 'md' }} gap="sm">
                <Typography size="sm" weight="bold">
                  Scratch Pad
                </Typography>
                {isReadOnly && (
                  <Token color="gray" data-testid="read-only-label">
                    Read Only
                  </Token>
                )}
              </Group>
              {lastStashedGroup && !isReadOnly && (
                <div className={styles.scratchpadRestoreBar}>
                  <Button tertiary data-testid="scratchpad-restore-button" size="sm" onClick={restoreLastStashedGroup}>
                    {`Restore ${lastStashedGroup.groupLabel}`}
                  </Button>
                </div>
              )}
              <div className={styles.scratchpadDropZone}>
                {isRefetching ? (
                  <ResponsiveLoader />
                ) : (
                  <Droppable droppableId={DROPPABLE_ID_PREFIXES.SCRATCHPAD} isDropDisabled={isReadOnly}>
                    {(droppableProvided) => (
                      <div
                        ref={droppableProvided.innerRef}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: '50px',
                          gap: 4
                        }}
                        {...droppableProvided.droppableProps}
                      >
                        {scratchpad.map((item, index) => (
                          <Draggable
                            draggableId={`${DRAGGABLE_PREFIXES.SCRATCHPAD_ITEM}${JSON.stringify(item)}`}
                            key={`${DRAGGABLE_PREFIXES.SCRATCHPAD_ITEM}${JSON.stringify(item)}`}
                            index={index}
                            isDragDisabled={isReadOnly}
                          >
                            {(draggableProvided, snapshot) => {
                              if (snapshot.isDragging) {
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore types are wrong here
                                const offsetX = draggableProvided.draggableProps.style.left - x;

                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore types are wrong here
                                // eslint-disable-next-line no-multi-assign, no-param-reassign
                                draggableProvided.draggableProps.style = draggableProvided.draggableProps.style = {
                                  ...draggableProvided.draggableProps.style,
                                  left: offsetX,
                                  top: CARD_HEIGHT * (index + 1) - scrollTop - SCRATCHPAD_TOP_CONTENT_OFFSET
                                };
                              }

                              return (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                >
                                  {item.type === EntityType.LinkedItem ? (
                                    <LinkedItem
                                      data={
                                        item as unknown as LinkedItemModel | MobileLinkedItem | GraphicLinkedItemModel
                                      }
                                      parentHierarchyId=""
                                      index={index}
                                      isDraft={false}
                                      isHistory
                                    />
                                  ) : (
                                    <Article
                                      data={item as ArticleItem}
                                      rulesConfig={rulesConfig}
                                      isHistory={false}
                                      isDraft={false}
                                      renderActions={() =>
                                        renderMenuActions(MenuActionsKeys.ArticleSearch, { entity: item })
                                      }
                                    />
                                  )}
                                </div>
                              );
                            }}
                          </Draggable>
                        ))}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                )}
              </div>
            </div>
          </div>
        </Rnd>
      </div>
    );
  }

  return null;
};

Scratchpad.displayName = 'Scratchpad';

export default Scratchpad;
